import { useState, useMemo } from 'react';
import { demoRooms } from '@/mocks/demoData';
import { useApp } from '@/contexts/AppContext';
import { addActivityLog } from '@/utils/activityLogStore';

interface HousekeepingTask {
  id: string;
  room_number: string;
  room_category: string;
  task_type: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed';
  assigned_to: string;
  notes: string;
  created_at: string;
  completed_at: string;
}

const TASK_TYPES = ['Full Cleaning', 'Quick Refresh', 'Deep Clean', 'Linen Change', 'Bathroom Clean', 'Turn Down', 'Post Checkout'];

const mockTasks: HousekeepingTask[] = [
  { id: 'hk-001', room_number: '103', room_category: 'Deluxe AC', task_type: 'Full Cleaning', priority: 'high', status: 'in_progress', assigned_to: 'Sunita Devi', notes: 'Guest checked out, prepare for next arrival', created_at: '2026-05-15T08:00:00Z', completed_at: '' },
  { id: 'hk-002', room_number: '304', room_category: 'Standard AC', task_type: 'Post Checkout', priority: 'high', status: 'pending', assigned_to: 'Sunita Devi', notes: 'Check-out from room 304', created_at: '2026-05-15T11:00:00Z', completed_at: '' },
  { id: 'hk-003', room_number: '101', room_category: 'Standard AC', task_type: 'Quick Refresh', priority: 'medium', status: 'pending', assigned_to: 'Sunita Devi', notes: 'Daily refresh for occupied room', created_at: '2026-05-15T07:00:00Z', completed_at: '' },
  { id: 'hk-004', room_number: '202', room_category: 'Suite', task_type: 'Turndown', priority: 'low', status: 'pending', assigned_to: 'Sunita Devi', notes: 'Evening turndown service', created_at: '2026-05-15T14:00:00Z', completed_at: '' },
  { id: 'hk-005', room_number: '402', room_category: 'Suite', task_type: 'Deep Clean', priority: 'medium', status: 'completed', assigned_to: 'Sunita Devi', notes: 'Weekly deep cleaning', created_at: '2026-05-14T09:00:00Z', completed_at: '2026-05-14T12:30:00Z' },
  { id: 'hk-006', room_number: '302', room_category: 'Deluxe AC', task_type: 'Linen Change', priority: 'medium', status: 'pending', assigned_to: 'Sunita Devi', notes: 'Guest requested fresh linens', created_at: '2026-05-15T10:00:00Z', completed_at: '' },
  { id: 'hk-007', room_number: '104', room_category: 'Deluxe AC', task_type: 'Bathroom Clean', priority: 'low', status: 'completed', assigned_to: 'Sunita Devi', notes: 'Deep bathroom cleaning', created_at: '2026-05-15T07:00:00Z', completed_at: '2026-05-15T08:30:00Z' },
];

const PRIORITY_BADGES: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-green-100 text-green-700',
};

const STATUS_BADGES: Record<string, string> = {
  pending: 'bg-navy-100 text-navy-600',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
};

export default function Housekeeping() {
  const { addToast } = useApp();
  const [tasks, setTasks] = useState<HousekeepingTask[]>(mockTasks);
  const [showAdd, setShowAdd] = useState(false);
  const [newTask, setNewTask] = useState({ room_number: '', task_type: 'Full Cleaning', priority: 'medium' as 'high' | 'medium' | 'low', assigned_to: 'Sunita Devi', notes: '' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (filterStatus !== 'all' && t.status !== filterStatus) return false;
      if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
      return true;
    }).sort((a, b) => {
      const p = { high: 0, medium: 1, low: 2 };
      return (p[a.priority] || 0) - (p[b.priority] || 0);
    });
  }, [tasks, filterStatus, filterPriority]);

  const stats = {
    total: tasks.length,
    pending: tasks.filter((t) => t.status === 'pending').length,
    inProgress: tasks.filter((t) => t.status === 'in_progress').length,
    completed: tasks.filter((t) => t.status === 'completed').length,
  };

  const handleAddTask = () => {
    if (!newTask.room_number) { addToast('error', 'Select a room'); return; }
    const room = demoRooms.find((r) => r.room_number === newTask.room_number);
    const task: HousekeepingTask = {
      id: `hk-${Date.now()}`,
      room_number: newTask.room_number,
      room_category: room?.category || '',
      task_type: newTask.task_type,
      priority: newTask.priority,
      status: 'pending',
      assigned_to: newTask.assigned_to,
      notes: newTask.notes,
      created_at: new Date().toISOString(),
      completed_at: '',
    };
    setTasks([task, ...tasks]);
    addActivityLog({
      user_id: 'usr-005',
      user_name: 'Sunita Devi',
      action: 'Housekeeping Task Created',
      entity_type: 'housekeeping',
      entity_id: task.id,
      details: { room: task.room_number, task: task.task_type },
    });
    addToast('success', `Task created for Room ${task.room_number}`);
    setShowAdd(false);
    setNewTask({ room_number: '', task_type: 'Full Cleaning', priority: 'medium', assigned_to: 'Sunita Devi', notes: '' });
  };

  const markComplete = (task: HousekeepingTask) => {
    setTasks(tasks.map((t) => t.id === task.id ? { ...t, status: 'completed', completed_at: new Date().toISOString() } : t));
    addActivityLog({
      user_id: 'usr-005',
      user_name: 'Sunita Devi',
      action: 'Housekeeping Task Completed',
      entity_type: 'housekeeping',
      entity_id: task.id,
      details: { room: task.room_number, task: task.task_type },
    });
    addToast('success', `Room ${task.room_number} ${task.task_type} completed`);
  };

  const startTask = (task: HousekeepingTask) => {
    setTasks(tasks.map((t) => t.id === task.id ? { ...t, status: 'in_progress' } : t));
    addToast('info', `Started Room ${task.room_number} — ${task.task_type}`);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Housekeeping</h1>
          <p className="text-navy-500 mt-1">Manage room cleaning tasks, assign staff, and track completion.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line mr-1" /> New Task
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Total Tasks</div>
          <div className="text-2xl font-bold text-navy-800">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Pending</div>
          <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">In Progress</div>
          <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4">
          <div className="text-xs text-navy-500 mb-1">Completed Today</div>
          <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All</option><option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Priority</label>
            <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Task Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((task) => (
          <div key={task.id} className={`bg-white rounded-xl border p-5 transition-all ${task.status === 'completed' ? 'border-green-200 opacity-75' : 'border-navy-100 hover:border-navy-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_BADGES[task.priority]}`}>{task.priority.toUpperCase()}</span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_BADGES[task.status]}`}>{task.status.replace('_', ' ')}</span>
            </div>
            <h3 className="font-semibold text-navy-800 text-lg mb-1">{task.task_type}</h3>
            <div className="text-sm text-navy-500 mb-3">Room {task.room_number} — {task.room_category}</div>
            <div className="flex items-center gap-2 text-sm text-navy-600 mb-2">
              <i className="ri-user-line" /> <span>{task.assigned_to}</span>
            </div>
            {task.notes && <div className="text-xs text-navy-400 mb-3 bg-navy-50 p-2 rounded-lg">{task.notes}</div>}
            <div className="flex items-center justify-between text-xs text-navy-400">
              <span>{new Date(task.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
              {task.completed_at && (
                <span className="text-green-600">
                  <i className="ri-check-line mr-1" /> {new Date(task.completed_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
            {task.status !== 'completed' && (
              <div className="flex gap-2 mt-4 pt-3 border-t border-navy-100">
                {task.status === 'pending' && (
                  <button onClick={() => startTask(task)} className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors cursor-pointer">
                    <i className="ri-play-line mr-1" /> Start
                  </button>
                )}
                {task.status === 'in_progress' && (
                  <button onClick={() => markComplete(task)} className="flex-1 px-3 py-2 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors cursor-pointer">
                    <i className="ri-check-line mr-1" /> Complete
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Task Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">New Housekeeping Task</h3>
              <button onClick={() => setShowAdd(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Room *</label>
                <select value={newTask.room_number} onChange={(e) => setNewTask({ ...newTask, room_number: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  <option value="">Select Room...</option>
                  {demoRooms.map((r) => <option key={r.id} value={r.room_number}>Room {r.room_number} — {r.category} ({r.status})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Task Type</label>
                <select value={newTask.task_type} onChange={(e) => setNewTask({ ...newTask, task_type: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  {TASK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Priority</label>
                <select value={newTask.priority} onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as 'high' | 'medium' | 'low' })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Assign To</label>
                <select value={newTask.assigned_to} onChange={(e) => setNewTask({ ...newTask, assigned_to: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                  <option value="Sunita Devi">Sunita Devi</option>
                  <option value="Ramesh Kumar">Ramesh Kumar</option>
                  <option value="Lakshmi Bai">Lakshmi Bai</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-1.5">Notes</label>
                <textarea rows={2} value={newTask.notes} onChange={(e) => setNewTask({ ...newTask, notes: e.target.value })} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowAdd(false)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleAddTask} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">Create Task</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}